import { HttpStatusCode, RulebookStatus, RulebookVisibility, } from "@next-platform/packages-ts-types";
import { axiosClient, getBaseUrlVersioned } from "../../services/axiosClient";
/** Largest page `GET /rulebooks` serves; fewer round trips for creators with many Rulebooks. */
const LIST_PAGE_SIZE = 50;
const rulebooksUrl = (path = "") => `${getBaseUrlVersioned(null)}/rulebooks${path}`;
/**
 * `GET /rulebooks` — every Rulebook the signed-in user owns, as cards. Pages are newest-first by `updatedAt`, but
 * the picker still needs the whole list to recognise an attached Rulebook. Only a cursor the server already
 * returned stops it early, so a misbehaving backend can't loop forever.
 */
export async function listMyRulebooks() {
    const seenCursors = new Set();
    const fetchFrom = async (cursor) => {
        const response = await axiosClient.get(rulebooksUrl(), {
            params: { limit: LIST_PAGE_SIZE, ...(cursor ? { cursor } : {}) },
        });
        const next = response.data.cursor;
        if (!next || seenCursors.has(next)) {
            return response.data.items;
        }
        seenCursors.add(next);
        return [...response.data.items, ...(await fetchFrom(next))];
    };
    return fetchFrom(undefined);
}
/**
 * `GET /rulebooks/{id}` as a card, for an attached or picked Rulebook that isn't among the user's own (a public one).
 * The owner gets the full Rulebook; anyone else the public view (hidden meters left out of the count). `null` when the
 * Rulebook is gone or not visible to the user (404).
 */
export async function getRulebookCard(rulebookId) {
    try {
        const response = await axiosClient.get(rulebooksUrl(`/${encodeURIComponent(rulebookId)}`));
        const rulebook = response.data;
        return {
            id: rulebook.id,
            name: rulebook.name,
            description: rulebook.description,
            tags: rulebook.tags,
            cover_url: rulebook.cover_url,
            visibility: rulebook.visibility ?? RulebookVisibility.PUBLIC,
            status: rulebook.status ?? RulebookStatus.ACTIVE,
            version: rulebook.version,
            num_meters: rulebook.num_meters ?? rulebook.meters.length,
            creatorUsername: rulebook.creator_username,
            isCoverNsfw: rulebook.cover_is_nsfw,
        };
    }
    catch (error) {
        if (error?.response?.status === HttpStatusCode.NOT_FOUND) {
            return null;
        }
        throw error;
    }
}
/** `GET /rulebooks/attachments` — the Rulebook attached to a character or persona, or `null`. */
export async function getRulebookAttachment({ ownerType, ownerId, }) {
    const response = await axiosClient.get(rulebooksUrl("/attachments"), {
        params: { owner_type: ownerType, owner_id: ownerId },
    });
    return response.data.attachment;
}
/** `POST /rulebooks/{id}/attach` — attaches (or replaces the owner's current Rulebook: one per owner). */
export async function attachRulebook({ rulebookId, ownerType, ownerId, }) {
    const response = await axiosClient.post(rulebooksUrl(`/${encodeURIComponent(rulebookId)}/attach`), {
        owner_type: ownerType,
        owner_id: ownerId,
    });
    return response.data;
}
/** `DELETE /rulebooks/{id}/attach/{owner_type}/{owner_id}`. */
export async function detachRulebook({ rulebookId, ownerType, ownerId, }) {
    await axiosClient.delete(rulebooksUrl(`/${encodeURIComponent(rulebookId)}/attach/${ownerType}/${encodeURIComponent(ownerId)}`));
}
/**
 * Brings a character's or persona's attachment from `initialRulebookId` to `currentRulebookId` after the owner was
 * saved: attach replaces any existing attachment server-side, so switching Rulebooks is a single call; clearing detaches.
 */
export async function applyRulebookAttachmentChange({ ownerType, ownerId, initialRulebookId, currentRulebookId, }) {
    if (initialRulebookId === currentRulebookId) {
        return;
    }
    if (currentRulebookId) {
        await attachRulebook({ rulebookId: currentRulebookId, ownerType, ownerId });
        return;
    }
    if (initialRulebookId) {
        await detachRulebook({ rulebookId: initialRulebookId, ownerType, ownerId });
    }
}
